import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Save } from "lucide-react";

// Hide while typing on small screens so the keyboard + bar never permanently cover the field.
function useTypingOnMobile() {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
    const on = (e) => setTyping(isField(e.target) && window.innerWidth < 768);
    const off = () => setTimeout(() => setTyping(isField(document.activeElement) && window.innerWidth < 768), 0);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);
  return typing;
}

export const ConfirmBar = ({ pending, canConfirm, confirming, onReview, onConfirm, notice }) => {
  const typing = useTypingOnMobile();
  const ready = pending === 0;
  return (
    <div data-testid="confirm-bar" data-ready={ready} aria-live="polite"
      className={`fixed inset-x-0 bottom-0 z-30 border-t border-[#E4E0D8] bg-white/95 backdrop-blur-md shadow-[0_-4px_16px_rgba(31,41,51,0.06)] transition-transform duration-200 ${typing ? "translate-y-full" : "translate-y-0"}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="max-w-[640px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {ready ? (
            <p className="text-[14px] font-semibold text-archival-primary flex items-center gap-1.5" data-testid="confirm-bar-status">
              <CheckCircle2 size={16} className="text-[var(--action)]" aria-hidden />All items ready
            </p>
          ) : (
            <>
              <p className="text-[12px] text-archival-secondary flex items-center gap-1" data-testid="draft-saved"><Save size={12} aria-hidden />Draft saved</p>
              <p className="text-[14px] font-semibold text-amber-800" data-testid="confirm-bar-status">
                {pending} {pending === 1 ? "item needs" : "items need"} information
              </p>
            </>
          )}
        </div>
        {notice ? (
          <p className="text-[13px] font-semibold text-red-800 text-right" data-testid="confirm-bar-notice">{notice}</p>
        ) : !canConfirm ? (
          <p className="text-[13px] text-archival-secondary" data-testid="confirm-bar-readonly">View only</p>
        ) : ready ? (
          <button type="button" onClick={onConfirm} disabled={confirming} data-testid="confirm-bill-btn"
            className="h-12 px-6 rounded-md text-white text-[15px] font-semibold disabled:opacity-60 shrink-0 transition-colors hover:brightness-95"
            style={{ backgroundColor: "var(--action)" }}>
            {confirming ? <Loader2 size={16} className="animate-spin" /> : "Confirm Bill"}
          </button>
        ) : (
          <button type="button" onClick={onReview} data-testid="review-items-btn"
            className="h-12 px-5 rounded-md border-2 border-amber-400 bg-amber-50 text-[15px] font-semibold text-amber-900 shrink-0">
            Review {pending} {pending === 1 ? "item" : "items"}
          </button>
        )}
      </div>
    </div>
  );
};
